import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import type { AdminEventFilters } from "@/features/admin/events/api/adminEventsApi";
import { useAdminEvents } from "@/features/admin/events/api/adminEventsApi";
import { ADMIN_EVENTS_COLUMNS, AdminEventsTable } from "@/features/admin/events/components/AdminEventsTable";
import type { AdminEventsFilterValues } from "@/features/admin/events/components/AdminEventsToolbar";
import {
    emptyAdminEventsFilterValues,
    AdminEventsToolbar,
} from "@/features/admin/events/components/AdminEventsToolbar";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const EventsSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    diaryId: z.string().optional(),
    organisationId: z.string().optional(),
    hasTrackers: z.enum(["all", "yes", "no"]).optional(),
});

export const Route = createFileRoute("/_app/admin/events")({
    component: AdminEventsPage,
    validateSearch: (search) => EventsSearchZ.parse(search),
});

function AdminEventsPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Events", route: { to: "/admin/events" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const [filterValues, setFilterValues] = useState<AdminEventsFilterValues>(() => ({
        ...emptyAdminEventsFilterValues,
        diaryId: search.diaryId ?? null,
        organisationId: search.organisationId ?? null,
        hasTrackers: search.hasTrackers ?? "all",
    }));

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_EVENTS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: 1,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    // Keep local filter state in sync when diaryId arrives via cross-link
    useEffect(() => {
        setFilterValues((prev) => ({
            ...prev,
            diaryId: search.diaryId ?? null,
            organisationId: search.organisationId ?? null,
            hasTrackers: search.hasTrackers ?? "all",
        }));
    }, [search.diaryId, search.organisationId, search.hasTrackers]);

    const filters: AdminEventFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            diaryId: filterValues.diaryId ?? undefined,
            organisationId: filterValues.organisationId ?? undefined,
            hasTrackers:
                filterValues.hasTrackers === "yes" ? true : filterValues.hasTrackers === "no" ? false : undefined,
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminEvents(filters);
    const events = data?.events ?? [];

    return (
        <AdminPageLayout
            title="Events"
            description="Browse clinical events across diaries. Filter by diary, organisation, or tracker link."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs
                    actions={
                        <AdminEventsToolbar
                            searchText={searchText}
                            onSearchTextChange={setSearchText}
                            filterValues={filterValues}
                            onApplyFilters={(values) => {
                                setFilterValues(values);
                                void navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        page: 1,
                                        diaryId: values.diaryId ?? undefined,
                                        organisationId: values.organisationId ?? undefined,
                                        hasTrackers: values.hasTrackers === "all" ? undefined : values.hasTrackers,
                                    }),
                                    replace: true,
                                });
                            }}
                            isApplying={isFetching}
                        >
                            <AdminColumnsDialog
                                columns={ADMIN_EVENTS_COLUMNS}
                                visibleKeys={visibleKeys}
                                onSave={setVisibleKeys}
                            />
                        </AdminEventsToolbar>
                    }
                />

                {filterValues.diaryId ? (
                    <p className="text-muted-foreground text-xs">
                        Filtered to diary <span className="font-mono">{filterValues.diaryId}</span>
                        {" · "}
                        <button
                            type="button"
                            className="text-sky-700 underline"
                            onClick={() => {
                                const cleared = { ...filterValues, diaryId: null };
                                setFilterValues(cleared);
                                void navigate({
                                    search: (prev) => ({ ...prev, diaryId: undefined, page: 1 }),
                                    replace: true,
                                });
                            }}
                        >
                            Clear diary filter
                        </button>
                    </p>
                ) : null}

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading events: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminEventsTable events={events} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
